import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { subscribeNewsletter } from "@/lib/api";
import { toast } from "sonner";
import { openCookiePreferences } from "@/components/CookieBanner";

const Footer = () => {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await subscribeNewsletter(email, lang);
      toast.success(t.footer.newsletter.success);
      setEmail("");
    } catch (_err) {
      toast.error(lang === "fr" ? "Email invalide" : "Invalid email");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#ebe7df] text-[#1A1A1A] mt-24"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 md:px-16 pt-20 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-10 lg:gap-16">
          {/* Brand + Newsletter */}
          <div className="md:col-span-5">
            <div className="font-serif-display text-3xl md:text-4xl tracking-tight">
              myFLOVEA
            </div>
            <p className="mt-3 text-[15px] text-[#4a4a4a] max-w-sm leading-relaxed">
              {t.footer.tagline}
            </p>

            <form
              onSubmit={handleSubmit}
              data-testid="newsletter-form"
              className="mt-10 max-w-sm"
            >
              <div className="eyebrow mb-2">{t.footer.newsletter.title}</div>
              <p className="text-sm text-[#4a4a4a] mb-4 leading-relaxed">
                {t.footer.newsletter.sub}
              </p>
              <div className="flex items-center border-b border-[#1A1A1A]">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.footer.newsletter.placeholder}
                  data-testid="newsletter-input"
                  className="flex-1 bg-transparent py-3 outline-none placeholder:text-[#4a4a4a]/70 text-[15px]"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="newsletter-submit"
                  className="pl-3 pr-1 py-3 disabled:opacity-50"
                  aria-label={t.footer.newsletter.submit}
                >
                  <ArrowRight size={18} strokeWidth={1.4} />
                </button>
              </div>
            </form>
          </div>

          {/* Columns */}
          <div className="md:col-span-2">
            <div className="eyebrow mb-5">{t.footer.columns.shop}</div>
            <ul className="space-y-3 text-[15px]">
              <li>
                <Link
                  to="/product/coussin-massant-chauffant"
                  data-testid="footer-link-cushion"
                  className="hover:opacity-60 transition-opacity"
                >
                  {t.footer.links.cushion}
                </Link>
              </li>
              <li>
                <Link
                  to="/product/sphere-duo"
                  data-testid="footer-link-sphere"
                  className="hover:opacity-60 transition-opacity"
                >
                  {lang === "fr" ? "La Sphère Duo" : "The Duo Sphere"}
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="eyebrow mb-5">{t.footer.columns.about}</div>
            <ul className="space-y-3 text-[15px]">
              <li>
                <Link to="/story" data-testid="footer-link-story" className="hover:opacity-60">
                  {t.footer.links.story}
                </Link>
              </li>
              <li>
                <Link to="/blog/douleurs-dos-tensions-bureau-solutions" data-testid="footer-link-blog" className="hover:opacity-60">
                  {lang === "fr" ? "Journal — Bien-être au bureau" : "Journal — Wellness at work"}
                </Link>
              </li>
              <li>
                <Link to="/contact" data-testid="footer-link-contact" className="hover:opacity-60">
                  {t.footer.links.contact}
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="eyebrow mb-5">{t.footer.columns.help}</div>
            <ul className="space-y-3 text-[15px]">
              <li>
                <Link to="/faq" data-testid="footer-link-faq" className="hover:opacity-60">
                  {t.footer.links.faq}
                </Link>
              </li>
              <li>
                <Link to="/legal/cgv" data-testid="footer-link-cgv" className="hover:opacity-60">
                  {t.footer.links.cgv}
                </Link>
              </li>
              <li>
                <Link to="/legal/retractation" data-testid="footer-link-retractation" className="hover:opacity-60">
                  {t.footer.links.retractation}
                </Link>
              </li>
              <li>
                <Link to="/legal/returns" data-testid="footer-link-returns" className="hover:opacity-60">
                  {t.footer.links.returns}
                </Link>
              </li>
              <li>
                <Link to="/legal/notice" data-testid="footer-link-legal" className="hover:opacity-60">
                  {t.footer.links.legal}
                </Link>
              </li>
              <li>
                <Link to="/legal/privacy" data-testid="footer-link-privacy" className="hover:opacity-60">
                  {lang === "fr" ? "Confidentialité" : "Privacy"}
                </Link>
              </li>
              <li>
                <Link to="/legal/cookies" data-testid="footer-link-cookies-policy" className="hover:opacity-60">
                  {lang === "fr" ? "Politique cookies" : "Cookie policy"}
                </Link>
              </li>
              <li>
                <Link to="/me/data" data-testid="footer-link-mydata" className="hover:opacity-60">
                  {lang === "fr" ? "Mes données (RGPD)" : "My data (GDPR)"}
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={openCookiePreferences}
                  data-testid="footer-manage-cookies"
                  className="hover:opacity-60 text-left"
                >
                  {lang === "fr" ? "Gérer mes cookies" : "Manage my cookies"}
                </button>
              </li>
              <li>
                <a
                  href="https://consumer-redress.ec.europa.eu/dispute-resolution-bodies_en"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-link-odr"
                  className="hover:opacity-60"
                >
                  {lang === "fr" ? "Résolution des litiges (UE)" : "EU dispute resolution"}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-[#dcd7ce] mt-16 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-[#4a4a4a]">
          <div>
            <div>{t.footer.rights}</div>
            <div className="mt-1 text-[11px] opacity-80">
              EM GROUP SASU · 60 Rue François Ier, 75008 Paris · SIRET 100 603 026 00012 · TVA FR89100603026
            </div>
          </div>
          <div className="tracking-[0.18em] uppercase">EU · 5–10 days</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
